import {
  clearFilter,
  isColumnFiltered,
} from "library/greco-components/column-options/utils";
import { Filter } from "library/types/columns";

type StateColumn = {
  key: string;
  isVisible: boolean;
  isFixed?: boolean;
  filter?: Filter;
};

/** Only visible columns may filter the offer list. */
export const toVisibleFilterColumns = <T extends { isVisible?: boolean }>(
  columns: T[]
): T[] => columns.filter((c) => c.isVisible !== false);

/** Clears the filter of every hidden column, so hidden columns never filter. */
export const clearHiddenFilters = <T extends StateColumn>(columns: T[]): T[] =>
  columns.map((c) =>
    c.isVisible === false && isColumnFiltered(c.filter)
      ? { ...c, filter: clearFilter(c.filter) }
      : c
  );

const toTime = (value: Date | string | null): number | null => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

/**
 * Reduces a filter to the values that reach the search query, so two filters
 * that query the same thing compare equal regardless of representation
 * (Date vs ISO string, selection order, cleared vs absent).
 */
export const normalizeFilter = (filter: Filter | undefined): unknown => {
  if (!filter || !isColumnFiltered(filter)) return null;
  switch (filter.type) {
    case "taxonomy":
      return { type: "taxonomy", value: [...filter.value].map(String).sort() };
    case "text":
    case "boolean":
      return { type: filter.type, value: filter.value };
    case "date":
      return {
        type: "date",
        operator: filter.operator,
        date1: toTime(filter.date1),
        date2: filter.operator === "isBetween" ? toTime(filter.date2) : null,
      };
    case "number": {
      const between = filter.operator === "between";
      return {
        type: "number",
        operator: filter.operator,
        number1: String(filter.number1),
        includeNumber1: !!filter.includeNumber1,
        number2: between ? String(filter.number2) : null,
        includeNumber2: between ? !!filter.includeNumber2 : false,
      };
    }
    default:
      return null;
  }
};

/** Visible column order plus the normalized filters of visible columns. */
export const columnsSignature = (columns: StateColumn[]): string =>
  JSON.stringify(
    columns
      .filter((c) => c.isVisible !== false)
      .map((c) => [c.key, normalizeFilter(c.filter)])
  );

export const areColumnsEquivalent = (
  a: StateColumn[],
  b: StateColumn[]
): boolean => columnsSignature(a) === columnsSignature(b);

/**
 * Shows or hides the given columns in one pass. Newly shown columns move to the
 * end (in the given order); newly hidden columns lose their filter. Fixed
 * columns are never changed.
 */
export const setVisibility = <T extends StateColumn>(
  columns: T[],
  keys: string[],
  visible: boolean
): T[] => {
  const changing = new Set(
    columns
      .filter(
        (c) =>
          keys.includes(c.key) && !c.isFixed && (c.isVisible !== false) !== visible
      )
      .map((c) => c.key)
  );
  if (changing.size === 0) return columns;
  if (!visible) {
    return columns.map((c) =>
      changing.has(c.key)
        ? { ...c, isVisible: false, filter: clearFilter(c.filter) }
        : c
    );
  }
  const shown = keys
    .filter((key) => changing.has(key))
    .map((key) => columns.find((c) => c.key === key))
    .filter((c): c is T => !!c)
    .map((c) => ({ ...c, isVisible: true }));
  return [...columns.filter((c) => !changing.has(c.key)), ...shown];
};
